import { useCallback, useEffect, useRef } from 'react';
import { useKeyboardNavigationContext } from './KeyboardNavigationContext';
import { generateId } from './helpers';
import type { AfterSelectAction } from './types';

interface Options {
	afterSelect?: AfterSelectAction;
}

export function useNavigableItem<T extends HTMLElement = HTMLElement>(
	onSelect: () => void,
	{ afterSelect = 'closeAndClear' }: Options = {},
) {
	const { activeItemId, registerNavigableItem, unregisterNavigableItem, onAfterSelect } =
		useKeyboardNavigationContext();

	const idRef = useRef(generateId());
	const itemRef = useRef<T>(null);

	// Read on each call, so `select` can stay the same function between renders.
	const latest = { onSelect, afterSelect, onAfterSelect };
	const latestRef = useRef(latest);
	latestRef.current = latest;

	const select = useCallback(() => {
		const current = latestRef.current;

		current.onSelect();

		if (current.afterSelect !== 'none') {
			current.onAfterSelect(current.afterSelect);
		}
	}, []);

	useEffect(() => {
		const id = idRef.current;

		registerNavigableItem(id, {
			ref: itemRef,
			select,
		});

		return () => unregisterNavigableItem(id);
	}, [registerNavigableItem, unregisterNavigableItem, select]);

	return {
		isActive: activeItemId === idRef.current,
		itemRef,
		select,
	};
}
